import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

const roles = [
  { key: "educator", label: "Educator / Individual" },
  { key: "school_leader", label: "School Leader" },
  { key: "district_leader", label: "District / System Leader" },
  { key: "admin", label: "Platform Admin" },
];

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "educator", org_name: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Account created");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7]">
      <div className="flex flex-col justify-center px-8 lg:px-16 py-16 order-2 lg:order-1">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="logo-link">
          <div className="h-10 w-10 rounded-xl bg-[#0B1E36] grid place-items-center">
            <Sparkles className="h-5 w-5 text-[#C9A961]" />
          </div>
          <div>
            <div className="font-serif text-lg text-[#0B1E36]">Future Counts OS™</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase">by the Collective</div>
          </div>
        </Link>
        <h1 className="font-serif text-4xl text-[#0B1E36] mb-2">Start your practice.</h1>
        <p className="text-slate-600 mb-8">Takes 60 seconds. Your baseline diagnostic is next.</p>
        <form onSubmit={onSubmit} className="max-w-md space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#0B1E36]">Full name</label>
            <input data-testid="signup-name" required value={form.name} onChange={upd("name")}
              className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#0B1E36]">Email</label>
            <input data-testid="signup-email" type="email" required value={form.email} onChange={upd("email")}
              className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#0B1E36]">Password</label>
            <input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={upd("password")}
              className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#0B1E36]">Your role</label>
            <select data-testid="signup-role" value={form.role} onChange={upd("role")}
              className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none bg-white">
              {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-[#0B1E36]">Organization <span className="font-normal text-slate-400">(optional)</span></label>
            <input data-testid="signup-org" value={form.org_name} onChange={upd("org_name")} placeholder="e.g. Lincoln USD"
              className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
          </div>
          <button data-testid="signup-submit" disabled={loading} type="submit"
            className="w-full bg-[#EC4899] text-white py-3.5 rounded-full font-semibold btn-lift hover:bg-[#DB2777] disabled:opacity-60 shadow-[0_8px_24px_rgba(236,72,153,0.25)]">
            {loading ? "Creating…" : "Create account"}
          </button>
          <div className="text-sm text-slate-600 text-center">
            Already a member? <Link to="/login" data-testid="to-login" className="text-[#0B1E36] font-semibold hover:underline">Sign in</Link>
          </div>
        </form>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1705909770198-7e83c24e1616?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
          alt="Workspace" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1E36]/70 to-transparent" />
        <div className="absolute top-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-widest text-[#C9A961] mb-3">Backed by research</div>
          <div className="font-serif text-3xl leading-tight max-w-md">Grounded in the emotional-intelligence framework school leaders trust.</div>
        </div>
      </div>
    </div>
  );
}
